import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@frontend-platform/ui-lib/ui';
import { ChevronDown, Info, MapPin } from 'lucide-react';
import { useState } from 'react';
import { ActionTag } from './action-tag';
import { getBandDisplay } from './band-config';
import { LinkChip } from './link-chip';
import { getLocationChipData } from './module-routes';
import type { RecommendationItem, SuggestedAction } from './types';
import { buildTooltipContent } from './utils';

interface SuggestionCardProps {
  item: RecommendationItem;
}

// One recommendation card. Ported (functionally and visually) from aistudio's
// SuggestionCardList.vue: title + impact badge, conversation/criteria counts
// with a metrics tooltip, description, and an expandable "Next steps" block
// with the fix, suggested actions (each a deep-link chip + action tag), and
// expected benefits.
export function SuggestionCard({ item }: SuggestionCardProps) {
  const [open, setOpen] = useState(false);
  const band = getBandDisplay(item.impactBand);
  const tooltipLines = buildTooltipContent(item.contributingMetrics ?? []);
  const showCounts = item.conversationCount != null || item.fromGoals != null;
  const actions = item.suggestedText ?? [];

  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="font-semibold text-base text-foreground">
          {item.title || item.issueType}
        </div>
        {band && (
          <span
            className={`shrink-0 rounded border px-2 py-0.5 font-medium text-xs ${band.classes}`}
          >
            {band.label}
          </span>
        )}
      </div>

      {showCounts && (
        <div className="mt-1.5 flex items-center gap-2 text-muted-foreground text-xs">
          {item.conversationCount != null && <span>{item.conversationCount} Conversations</span>}
          {item.conversationCount != null && item.fromGoals != null && <span>|</span>}
          {item.fromGoals != null && <span>{item.fromGoals} Criteria</span>}
          {tooltipLines.length > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3.5 w-3.5 cursor-pointer text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>
                {tooltipLines.map((line) => (
                  <div key={line}>{line}</div>
                ))}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      )}

      {item.issueDescription && (
        <p className="mt-3 text-foreground text-sm">{item.issueDescription}</p>
      )}

      <Collapsible open={open} onOpenChange={setOpen} className="mt-3">
        <CollapsibleTrigger className="flex items-center gap-1 font-semibold text-foreground text-sm">
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? '' : '-rotate-90'}`} />
          Next steps and problem areas:
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-4">
          {item.fix && <p className="mb-4 text-foreground text-sm">{item.fix}</p>}

          {actions.map((action, idx) => (
            <div key={`${item.issueId}-action-${idx}`}>
              <SuggestedActionRow action={action} />
              {idx !== actions.length - 1 && (
                <div className="my-3 border-border border-b border-dashed" />
              )}
            </div>
          ))}

          {item.benefits && item.benefits.length > 0 && (
            <>
              <div className="my-3 border-border border-b border-dashed" />
              <div className="w-fit min-w-70">
                <div className="rounded-t-md border border-[#DDE6CF] bg-background px-3 py-1.5 text-foreground text-sm">
                  Expected Benefits
                </div>
                <ol className="list-decimal space-y-2 rounded-b-md border border-[#DDE6CF] bg-[#FAFCF7] py-2 pr-3 pl-7 text-muted-foreground text-sm">
                  {item.benefits.map((benefit, bIdx) => (
                    <li key={`${item.issueId}-benefit-${bIdx}`}>{benefit}</li>
                  ))}
                </ol>
              </div>
            </>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

function SuggestedActionRow({ action }: { action: SuggestedAction }) {
  const chip = getLocationChipData(action);
  const hasTooltipContent = Boolean(action.quote || action.fieldLabel);

  return (
    <div>
      <div className="my-1 flex items-center gap-3">
        {chip &&
          (hasTooltipContent ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <LinkChip {...chip} />
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-70">
                {action.quote && (
                  <div className="font-semibold text-xs">
                    Current –<div className="mt-1 font-normal">{action.quote}</div>
                  </div>
                )}
                {action.quote && action.fieldLabel && (
                  <div className="my-2 border-white/20 border-t" />
                )}
                {action.fieldLabel && (
                  <div className="flex items-center gap-1 text-white/80 text-xs">
                    <MapPin className="h-3 w-3 shrink-0" />
                    <span>{action.fieldLabel}</span>
                  </div>
                )}
              </TooltipContent>
            </Tooltip>
          ) : (
            <LinkChip {...chip} />
          ))}
        <ActionTag action={action.action} />
      </div>
      {action.prompt && (
        <div className="mt-2 rounded-md border border-border bg-muted/40 p-2 text-foreground text-sm">
          {action.prompt}
        </div>
      )}
    </div>
  );
}
